import { extractCampaignId, mapRawToBuzzBridgeJob } from './mapBuzzBridgeJob';
import { plainTextFromHtml } from '../../../pages/BrandDashboard/contestWizard/richTextUtils';
import { normalizeUnreadCount } from '../../../utils/campaignThreadUnread';

export interface BuzzBridgeRequestRow {
    id: string;
    campaignId: string;
    /** Participation / request document id (for workspace when approved). */
    participantId: string;
    title: string;
    company: string;
    status: 'pending' | 'accepted' | 'rejected';
    budget: string;
    deadline: string;
    description: string;
    appliedDate: string;
    unreadMessageCount: number;
}

function fallbackFromRaw(r: Record<string, unknown>): {
    title: string;
    company: string;
    budget: string;
    deadline: string;
    description: string;
} {
    const campaign = (r.campaign && typeof r.campaign === 'object' ? r.campaign : {}) as Record<string, unknown>;
    const brand = (r.brand && typeof r.brand === 'object' ? r.brand : {}) as Record<string, unknown>;
    const title =
        (typeof campaign.title === 'string' && campaign.title) ||
        (typeof r.title === 'string' && r.title) ||
        'Campaign';
    const company =
        (typeof brand.name === 'string' && brand.name) ||
        (typeof campaign.brand_name === 'string' && campaign.brand_name) ||
        (typeof r.company === 'string' && r.company) ||
        '';
    const budgetVal = r.budget ?? campaign.budget ?? campaign.possible_earning;
    let budget = '—';
    if (typeof budgetVal === 'number' && !Number.isNaN(budgetVal)) {
        budget = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(budgetVal);
    } else if (typeof budgetVal === 'string' && budgetVal.trim()) {
        budget = budgetVal;
    }
    let deadline = '—';
    const dc = campaign.end_date ?? campaign.deadline ?? r.deadline;
    if (typeof dc === 'string' && dc) {
        const d = Date.parse(dc);
        deadline = Number.isNaN(d)
            ? dc
            : new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric' }).format(new Date(d));
    }
    const rawDesc =
        (typeof campaign.description === 'string' && campaign.description) ||
        (typeof r.description === 'string' && r.description) ||
        '';
    const description = plainTextFromHtml(rawDesc).slice(0, 200);
    return { title, company, budget, deadline, description };
}

export function formatRawAppliedDateLine(raw: unknown): string {
    if (!raw || typeof raw !== 'object') return '';
    const d = formatAppliedDate(raw as Record<string, unknown>);
    return d !== '—' ? `Applied ${d}` : '';
}

function formatAppliedDate(raw: Record<string, unknown>): string {
    const candidates = [
        raw.applied_at,
        raw.appliedAt,
        raw.submitted_at,
        raw.submittedAt,
        raw.createdAt,
        raw.created_at,
    ];
    for (const v of candidates) {
        if (v == null) continue;
        if (typeof v === 'string' && v) {
            const d = Date.parse(v);
            if (!Number.isNaN(d)) {
                return new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric' }).format(
                    new Date(d),
                );
            }
            return v;
        }
    }
    return '—';
}

function normalizeStatus(raw: Record<string, unknown>): BuzzBridgeRequestRow['status'] {
    const s = String(raw.status ?? 'pending').toLowerCase();
    if (s.includes('accept') || s === 'approved') return 'accepted';
    if (s.includes('reject')) return 'rejected';
    return 'pending';
}

export function mapRawToBuzzBridgeRequest(raw: unknown): BuzzBridgeRequestRow | null {
    if (!raw || typeof raw !== 'object') return null;
    const r = raw as Record<string, unknown>;

    const job = mapRawToBuzzBridgeJob(r);
    const fb = fallbackFromRaw(r);
    const campaignId = extractCampaignId(r);
    const listId = String(r._id ?? r.id ?? campaignId ?? '');
    if (!listId) return null;

    const status = normalizeStatus(r);

    return {
        id: listId,
        campaignId: campaignId || listId,
        participantId: String(r._id ?? '').trim(),
        title: job?.title ?? fb.title,
        company: job?.company ?? fb.company,
        status,
        budget: job?.rewardDisplay ?? job?.budget ?? fb.budget,
        deadline: job?.deadline ?? fb.deadline,
        description: job?.description ?? fb.description,
        appliedDate: formatAppliedDate(r),
        unreadMessageCount: normalizeUnreadCount(r.unread_message_count),
    };
}
